import { ProcessStateT } from "../../interfaces";
import { useQuery } from "@tanstack/react-query";
import { workflowTwoApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsWorkflowTwoI,
  WorkflowTwoI,
  ObjPostI,
  WorkflowTwoForNextProcessI,
} from "../../interfaces";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

const getWorkflowTwo = async ({
  pagination,
  sorting,
  globalFilter,
  period,
}: PropsWorkflowTwoI): Promise<ServiceResponseI<DataI<WorkflowTwoI>>> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    period && params.append("period", period);

    //headers: { 'Authorization': validateToken
    const { data } = await workflowTwoApi.get<
      ServiceResponseI<DataI<WorkflowTwoI>>
    >("", { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    console.log(err);
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const getAllWorkflowTwo = async (): Promise<
  ServiceResponseI<WorkflowTwoI[]>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowTwoApi.get<ServiceResponseI<WorkflowTwoI[]>>(
      `/all-no-pagination`,
      { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const getWorkflowTwoByContainerForThree = async (
  container: string
): Promise<ServiceResponseI<WorkflowTwoI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowTwoApi.get<ServiceResponseI<WorkflowTwoI>>(
      `/by-container-for-three/${container}`,
      { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const getWorkflowTwoByContainerForNextProcess = async (
  container: string
): Promise<ServiceResponseI<WorkflowTwoForNextProcessI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowTwoApi.get<
      ServiceResponseI<WorkflowTwoForNextProcessI>
    >(`/by-container-for-next-process/${container}`, {
      headers: { Authorization: validateToken },
    });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

// export const workflowTwoPost = async <T>(
//   getForms: T
// ): Promise<ServiceResponseI<ObjPostI>> => {
//   try {
//     const validateToken = ValidateCurrentToken();
//     if (!validateToken) throw new Error("TOKEN INVALIDO");
//     const { data } = await workflowTwoApi.post<ServiceResponseI<ObjPostI>>(
//       "",
//       getForms,
//       { headers: { Authorization: validateToken } }
//     );
//     return data;
//   } catch (err: any) {
//     if (err.code === "ERR_NETWORK") {
//       return {
//         data: {
//           id: 0,
//           alert: "Se guardará y notificará, cuando haya conexión a Internet",
//           variant: "warning",
//         },
//         res: {} as any,
//         req: {} as any,
//       };
//     }
//     const obj = (err as AxiosError).response;
//     if (obj?.status === 401) {
//       console.error(obj.statusText);
//       LogoutSystem();
//     }
//     throw new Error((obj?.data as Error).message);
//   }
// };

export const workflowTwoPost = async <T>(
  getForms: T
): Promise<ServiceResponseI<ObjPostI>> => {
  try {
    // Validar el token como antes
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    // URL del endpoint - usando exactamente la misma URL que axios
    const url = `${process.env.REACT_APP_BACKEND!}api/workflow-two`;

    // Generar un ID único para esta transacción
    const offlineId = `offline_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    // Crear una promesa para manejar la comunicación con el Service Worker
    return new Promise((resolve, reject) => {
      // Verificar si el Service Worker está disponible
      if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
        console.warn(
          "Service Worker no está disponible, intentando petición normal"
        );
        // Hacer la petición normal como fallback
        workflowTwoApi
          .post<ServiceResponseI<ObjPostI>>("", getForms, {
            headers: { Authorization: validateToken },
          })
          .then(({ data }) => resolve(data))
          .catch((err) => {
            if (err.code === "ERR_NETWORK") {
              resolve({
                data: {
                  id: 0,
                  alert: "Error de red. Intenta de nuevo cuando haya conexión",
                  variant: "error",
                },
                res: {} as any,
                req: {} as any,
              });
            } else {
              reject(err);
            }
          });
        return;
      }

      // Crear un canal de mensajes para recibir la respuesta del Service Worker
      const messageChannel = new MessageChannel();

      // Escuchar la respuesta del Service Worker
      messageChannel.port1.onmessage = (event) => {
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          // Devolver una respuesta simulada para la UI con mejor información
          resolve({
            data: {
              id: parseInt(offlineId.split("_")[1]) || 0,
              alert: navigator.onLine
                ? "Datos guardados, sincronizando en segundo plano..."
                : "Datos guardados. Se sincronizarán cuando haya conexión.",
              variant: navigator.onLine ? "info" : "warning",
            },
            res: {} as any,
            req: {} as any,
            // isOffline: true,
          });
        }
      };

      // Enviar el mensaje al Service Worker
      navigator.serviceWorker.controller.postMessage(
        {
          type: "ENQUEUE_POST",
          url,
          data: getForms,
          offlineId,
          headers: {
            "Content-Type": "application/json",
            Authorization: validateToken,
            "X-Offline-ID": offlineId,
          },
        },
        [messageChannel.port2]
      );
    });
  } catch (err: any) {
    // Manejar errores específicos
    if (err.message === "TOKEN INVALIDO") {
      console.error("Token inválido");
      LogoutSystem();
      throw err;
    }

    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }

    // Si hay otro error
    return {
      data: {
        id: 0,
        alert: "Error al guardar los datos. Por favor, intenta de nuevo.",
        variant: "error",
      },
      res: {} as any,
      req: {} as any,
      // isOffline: true,
    };
  }
};

export const workflowTwoCount = async (
  type = "hoy"
): Promise<ServiceResponseI<{ total: number }>> => {
  try {
    const params = new URLSearchParams();

    type && params.append("type", type.toString());

    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowTwoApi.get<
      ServiceResponseI<{ total: number }>
    >(`/count`, { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowTwoPut = async <T>(
  id: number,
  updateForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowTwoApi.put<ServiceResponseI<string>>(
      `/${id}`,
      updateForms,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowTwoSendReportById = async <T>(
  getForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowTwoApi.post<ServiceResponseI<string>>(
      `/send-report-by-workflow-id`,
      getForms,
      {
        headers: { Authorization: validateToken },
      }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowTwoSetState = async (
  id: number,
  body: { state: ProcessStateT; reason?: string }
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowTwoApi.put<ServiceResponseI<string>>(
      `/${id}/state`,
      body,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};


export const workflowTwoById = async (
  id: number
): Promise<ServiceResponseI<WorkflowTwoI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowTwoApi.get<ServiceResponseI<WorkflowTwoI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowTwoForExternalById = async (
  id: string
): Promise<ServiceResponseI<WorkflowTwoI>> => {
  try {
    const encodedId = encodeURIComponent(id);
    const { data } = await workflowTwoApi.get<ServiceResponseI<WorkflowTwoI>>(
      `/external/${encodedId}`
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowTwoDelete = async (
  id: number
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowTwoApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const generateWfTwoPdfReportbyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowTwoApi.get<ServiceResponseI<any>>(
      `/generate-pdf-report/${id}`
      // { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const useWorkflowTwo = ({
  sorting,
  pagination,
  globalFilter,
  period,
}: PropsWorkflowTwoI) => {
  const workflowTwosQuery = useQuery(
    ["workflowTwo", { pagination, sorting, globalFilter, period }],
    () => getWorkflowTwo({ pagination, sorting, globalFilter, period }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  return {
    workflowTwosQuery,
  };
};